import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";
import { supabase } from "@/integrations/supabase/client";

/**
 * Three-button action row shown on every completed single verification.
 *
 *   1. Add to closet  → keeps the scan in approved_closet (private to user)
 *   2. Post to feed   → opens /post/quick prefilled with scan data
 *   3. Discard        → marks the scan discarded (hidden from closet)
 *
 * Single scans start as approved_closet immediately — they are never
 * pending. Discard is the only action that hides the scan from the closet.
 */
export interface ScanReviewActionsProps {
  scanId: string | null;
  // Data passed to /post/quick when the user chooses "Post to feed".
  postState: Record<string, unknown>;
  onActioned?: (kind: "closet" | "feed" | "discard") => void;
  /** When true, render compact (used inside review-queue cards). */
  compact?: boolean;
}

export function ScanReviewActions({
  scanId,
  postState,
  onActioned,
  compact,
}: ScanReviewActionsProps) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  const addToCloset = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (!scanId) throw new Error("Scan not saved yet — please wait a moment and try again");
      const { error } = await supabase
        .from("scans")
        .update({ review_status: "approved_closet", status: "kept", posted: false } as never)
        .eq("id", scanId);
      if (error) throw error;
      toast.success("Added to your closet");
      onActioned?.("closet");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't add to closet");
      setBusy(false);
    }
  };

  const postToFeed = () => {
    nav("/post/quick", { state: { fromScan: { ...postState, scanId } } });
    onActioned?.("feed");
  };

  const discard = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (scanId) {
        const { error } = await supabase
          .from("scans")
          .update({ review_status: "discarded", posted: false } as never)
          .eq("id", scanId);
        if (error) throw error;
      }
      toast("Discarded — scan saved to your history");
      onActioned?.("discard");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't discard");
      setBusy(false);
    }
  };

  return (
    <div role="group" aria-label="Review actions" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* 1. PRIMARY — Post to Feed */}
      <button
        className="btn btn-rust"
        style={{ width: "100%", padding: compact ? "10px" : "16px", fontSize: compact ? 13 : 15 }}
        onClick={postToFeed}
        disabled={busy}
      >
        Post to feed →
      </button>
      {/* 2. SECONDARY — Add to Closet */}
      <button
        onClick={addToCloset}
        disabled={busy}
        style={{
          width: "100%", padding: compact ? "10px" : "14px",
          background: "hsl(var(--cream))", color: "hsl(var(--ink))",
          border: "1px solid hsl(var(--ink))", cursor: busy ? "wait" : "pointer",
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
        }}
      >
        Add to closet
      </button>
      {/* 3. TERTIARY — Discard */}
      <button
        onClick={discard}
        disabled={busy}
        style={{
          width: "100%", padding: "10px",
          background: "transparent", color: "hsl(var(--ink-2))",
          border: "none", cursor: busy ? "wait" : "pointer",
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
          textDecoration: "underline",
        }}
      >
        Discard
      </button>
    </div>
  );
}